import { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import httpService from "../services/httpService";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from "react-bootstrap/Badge";
import Card from 'react-bootstrap/Card';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Collapse from 'react-bootstrap/Collapse';
import Form from 'react-bootstrap/Form';
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger";
import Tooltip from "react-bootstrap/esm/Tooltip";

import {
  FaPlay, FaPause, FaCogs, FaPlus, FaCog,
  FaExternalLinkAlt, FaStepBackward, FaQuoteRight,
  FaRegLightbulb, FaComments, FaCommentAlt,
  FaSearch, FaTimes, FaUsers,
} from "react-icons/fa";
import { FaWrench } from "react-icons/fa6";

import "../styles/dashbaords.scss";
import "../styles/ListenerEpisode.scss";
import LoadingFade from "../components/LoadingFade";
import KeyTakeawayImageWithPlaceholder from "../components/KeyTakeawayImageWithPlaceholder";
import PracticeModal from "../components/ListenerComponents/PracticeModal";
import ListenerEpisodePublicPractices from "../components/ListenerComponents/ListenerEpisodePublicPractices";
import { getImage } from "../utils/podcastClubUtils";
import { useAudioPlayerStore } from "../store/store";

/* ─── Helpers ─────────────────────────────────────── */
const getInitials = (user) => {
  if (!user) return '?';
  const f = user.first_name?.[0] || '';
  const l = user.last_name?.[0] || '';
  return (f + l).toUpperCase() || '?';
};

const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 7)  return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

/* ─── Component ───────────────────────────────────── */
const ListenerEpisode = () => {
  const { podcastClubId, episodeId } = useParams();
  const {
    audioUrl: globalAudioUrl, isPlaying,
    setAudioUrl, setFrom, setFeaturedImage,
    setEpisodeId: setGlobalEpisodeId,
    seekTo, setPlay, setPause,
  } = useAudioPlayerStore();

  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [pivot, setPivot]               = useState({});
  const [episode, setEpisode]           = useState({});
  const [podcastClub, setPodcastClub]   = useState({});
  const [skills, setSkills]             = useState([]);
  const [keyTakeaways, setKeyTakeaways] = useState([]);
  const [takeawaySearch, setTakeawaySearch] = useState('');
  const [discussions, setDiscussions]   = useState([]);
  const [openPractices, setOpenPractices] = useState({});
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [selectedPractice, setSelectedPractice]   = useState(null);
  const [selectedTakeawayId, setSelectedTakeawayId] = useState(null);
  const [selectedTakeaway, setSelectedTakeaway]   = useState(null);
  const [activeTab, setActiveTab]       = useState('takeaways');

  /* ── Practice handlers ── */
  const togglePractices = (id) =>
    setOpenPractices(prev => ({ ...prev, [id]: !prev[id] }));

  const openPracticeModal = (practice, takeaway) => {
    setSelectedPractice(practice);
    setSelectedTakeaway(takeaway);
    setSelectedTakeawayId(takeaway.id);
    setShowPracticeModal(true);
  };

  const closePracticeModal = () => {
    setShowPracticeModal(false);
    setSelectedPractice(null);
    setSelectedTakeawayId(null);
    setSelectedTakeaway(null);
  };

  const openCreatePracticeModal = (takeaway) => {
    setSelectedTakeawayId(takeaway.id);
    setSelectedTakeaway(takeaway);
    setSelectedPractice(null);
    setShowPracticeModal(true);
  };

  const handlePracticeChange = (saved) => {
    setKeyTakeaways(prev => prev.map(t => {
      if (t.id !== selectedTakeawayId) return t;
      const exists = (t.practices || []).some(p => p.id === saved.id);
      return {
        ...t,
        practices: exists
          ? t.practices.map(p => p.id === saved.id ? saved : p)
          : [...(t.practices || []), saved],
      };
    }));
  };

  const handlePracticeDeleted = (practiceId) => {
    setKeyTakeaways(prev => prev.map(t => {
      if (t.id !== selectedTakeawayId) return t;
      return { ...t, practices: (t.practices || []).filter(p => p.id !== practiceId) };
    }));
  };

  /* ── Audio helpers ── */
  const parseTimeToSeconds = (s) => {
    if (!s) return 0;
    const parts = s.split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return Number(s) || 0;
  };

  const playFromTime = (timeStr) => {
    if (!episode?.audio_url) return;
    if (globalAudioUrl !== episode.audio_url) {
      setAudioUrl(episode.audio_url);
      setFrom(episode.title || podcastClub.title || 'Now playing');
      setFeaturedImage(getImage(podcastClub, 'super_wide') || null);
      setGlobalEpisodeId(episodeId);
    } else {
      setPlay();
    }
    seekTo(parseTimeToSeconds(timeStr));
  };

  const handlePlayPause = () => {
    if (globalAudioUrl === episode.audio_url) {
      isPlaying ? setPause() : setPlay();
    } else {
      setAudioUrl(episode.audio_url);
      setFrom(episode.title || podcastClub.title || 'Now playing');
      setFeaturedImage(getImage(podcastClub, 'super_wide') || null);
      setGlobalEpisodeId(episodeId);
    }
  };

  /* ── Data loading ── */
  useEffect(() => {
    if (podcastClubId && episodeId) loadEpisode();
  }, [podcastClubId, episodeId]);

  const loadEpisode = () => {
    setError(null);
    httpService.get(`/dash/listener-podcast-club/${podcastClubId}/episode/${episodeId}`)
      .then(res => {
        const d = res.data.data;
        setPivot(d.pivot);
        setEpisode(d.episode);
        setSkills(d.skills || []);
        setKeyTakeaways(d.key_takeaways || []);
        setDiscussions(d.discussions || []);
        setPodcastClub(d.podcast_club);
      })
      .catch(err => setError(err.response?.data?.message || 'Error loading episode'))
      .finally(() => setLoading(false));
  };

  /* ── Derived ── */
  const isThisPlaying   = globalAudioUrl === episode?.audio_url && isPlaying;
  const filteredTakeaways = keyTakeaways.filter(t => {
    const q = takeawaySearch.toLowerCase();
    return !q || t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
  });
  const heroImage = getImage(podcastClub, 'super_wide');
  const clubUrl   = `/listener-podcast-club/${podcastClub.guid}`;

  /* ══════════════════════════════════════════════════
     Render
     ══════════════════════════════════════════════════ */
  return (
    <LoadingFade id="ListenerEpisode-component" loading={loading} stateKey={loading ? 'loading' : 'loaded'}>

      {/* ── Hero ──────────────────────────────────── */}
      <div
        className="ep-hero"
        style={heroImage ? { backgroundImage: `url(${heroImage})` } : { background: 'linear-gradient(135deg, #113F67 0%, #5A9CB5 100%)' }}
      >
        <div className="ep-hero__scrim">

          {/* Top bar: breadcrumb + back */}
          <div className="d-flex justify-content-between align-items-start">
            <NavLink className="ep-hero__breadcrumb" to={clubUrl}>
              ← {podcastClub.title}
            </NavLink>
            <a className="ep-hero__back-btn" href={clubUrl}>
              <FaStepBackward /> Back
            </a>
          </div>

          {/* Bottom: meta + title + play */}
          <div>
            {pivot?.start_date_time && (
              <div className="ep-hero__meta">
                {new Date(pivot.start_date_time).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </div>
            )}
            <h1 className="ep-hero__title">{pivot?.title || episode?.title}</h1>
            {episode?.audio_url && (
              <button type="button" className="ep-hero__play-btn" onClick={handlePlayPause}>
                {isThisPlaying ? <><FaPause />Pause</> : <><FaPlay />Play Episode</>}
              </button>
            )}
          </div>

        </div>
      </div>

      {/* ── Two-column content ─────────────────────── */}
      <div className="container-lg py-4 px-3 px-lg-4">
        <Row className="g-4">

          {/* Main column — tabs */}
          <Col lg={8}>
            <Tabs
              activeKey={activeTab}
              onSelect={k => setActiveTab(k)}
              id="episode-tabs"
              className="mb-3"
            >

              {/* ══ Insights tab ═══════════════════════ */}
              <Tab
                eventKey="takeaways"
                title={
                  <span>
                    <FaRegLightbulb className="me-1" />
                    Insights
                    {keyTakeaways.length > 0 && (
                      <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.62rem' }}>
                        {keyTakeaways.length}
                      </Badge>
                    )}
                  </span>
                }
              >
                {keyTakeaways.length > 0 ? (
                  <>
                    {/* Filter bar */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className="input-group input-group-sm" style={{ maxWidth: '320px' }}>
                        <span className="input-group-text"><FaSearch /></span>
                        <Form.Control
                          type="text"
                          placeholder="Filter insights…"
                          value={takeawaySearch}
                          onChange={e => setTakeawaySearch(e.target.value)}
                        />
                        {takeawaySearch && (
                          <Button variant="outline-secondary" size="sm" onClick={() => setTakeawaySearch('')}>
                            <FaTimes />
                          </Button>
                        )}
                      </div>
                      {takeawaySearch && (
                        <small className="text-muted">
                          {filteredTakeaways.length} of {keyTakeaways.length}
                        </small>
                      )}
                    </div>

                    {/* Card grid */}
                    <Row className="g-3">
                      {filteredTakeaways.map(takeaway => {
                        const practiceCount   = (takeaway.practices || []).length;
                        const completedCount  = (takeaway.practices || []).filter(p => p.completed).length;
                        const cardVariant     = practiceCount === 0
                          ? 'no-practices'
                          : (practiceCount === completedCount ? 'all-done' : '');

                        return (
                          <Col lg={6} key={takeaway.id} className="d-flex">
                            <Card className={`takeaway-card w-100 ${cardVariant}`}>

                              {/* Image + quote overlay */}
                              <div className="takeaway-card__img-wrap">
                                <KeyTakeawayImageWithPlaceholder image={takeaway.latest_image} />
                                {takeaway.quote && (
                                  <div className="takeaway-card__quote-pill">
                                    <FaQuoteRight style={{ fontSize: '0.58rem', opacity: 0.65, marginRight: '4px' }} />
                                    {takeaway.quote}
                                  </div>
                                )}
                              </div>

                              <Card.Body className="d-flex flex-column p-3">
                                <h3 className="takeaway-card__title mb-3">{takeaway.title}</h3>

                                {/* Practices collapse */}
                                <div className="practices-wrapper mt-auto">
                                  <Collapse in={!!openPractices[takeaway.id]}>
                                    <div id={`practices-${takeaway.id}`}>
                                      {(takeaway.practices || []).map(practice => (
                                        <div
                                          key={practice.id}
                                          className="p-2 mt-2 bg-light rounded d-flex justify-content-between align-items-center"
                                        >
                                          <div>
                                            <div className="d-flex align-items-center gap-2">
                                              <strong className="small">{practice.title || 'Practice'}</strong>
                                              <Badge
                                                bg={practice.completed ? 'success' : 'secondary'}
                                                style={{ fontSize: '0.58rem' }}
                                              >
                                                {practice.completed ? 'Done' : 'In Progress'}
                                              </Badge>
                                            </div>
                                            {practice.practice_date_time && (
                                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                {new Date(practice.practice_date_time).toLocaleDateString('en-US', {
                                                  month: 'short', day: 'numeric', year: 'numeric',
                                                })}
                                              </div>
                                            )}
                                          </div>
                                          <Button
                                            variant="outline-success"
                                            size="sm"
                                            onClick={() => openPracticeModal(practice, takeaway)}
                                          >
                                            <FaWrench />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </Collapse>
                                </div>
                              </Card.Body>

                              {/* Action footer */}
                              <Card.Footer className="takeaway-card__footer">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Practices</Tooltip>}>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className={`position-relative${practiceCount === 0 ? ' btn-zoomed-out' : ''}`}
                                    onClick={() => togglePractices(takeaway.id)}
                                    aria-controls={`practices-${takeaway.id}`}
                                    aria-expanded={!!openPractices[takeaway.id]}
                                    disabled={practiceCount === 0}
                                  >
                                    <FaCogs />
                                    <Badge
                                      bg="danger"
                                      pill
                                      className="position-absolute top-0 start-50 translate-middle"
                                      style={{ fontSize: '0.58rem' }}
                                    >
                                      {completedCount}/{practiceCount}
                                    </Badge>
                                  </Button>
                                </OverlayTrigger>

                                <OverlayTrigger placement="top" overlay={<Tooltip>Add practice</Tooltip>}>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    className={practiceCount === 0 ? 'btn-pulse' : ''}
                                    onClick={() => openCreatePracticeModal(takeaway)}
                                  >
                                    <FaCog /><FaPlus />
                                  </Button>
                                </OverlayTrigger>

                                {takeaway.start_time && (
                                  <OverlayTrigger placement="top" overlay={<Tooltip>Play at {takeaway.start_time}</Tooltip>}>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => playFromTime(takeaway.start_time)}
                                    >
                                      <FaPlay />
                                    </Button>
                                  </OverlayTrigger>
                                )}
                              </Card.Footer>

                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <FaRegLightbulb style={{ fontSize: '2.5rem', opacity: 0.2 }} />
                    <p className="mt-3 mb-0">No insights available yet.</p>
                  </div>
                )}
              </Tab>

              {/* ══ Public Practices tab ═══════════════ */}
              <Tab
                eventKey="public-practices"
                title={<span><FaUsers className="me-1" />Public Practices</span>}
              >
                {activeTab === 'public-practices' && (
                  <ListenerEpisodePublicPractices
                    podcastClubId={podcastClubId}
                    episodeId={episodeId}
                  />
                )}
              </Tab>

              {/* ══ Discussions tab ════════════════════ */}
              <Tab
                eventKey="discussions"
                title={
                  <span>
                    <FaComments className="me-1" />
                    Discussions
                    {discussions.length > 0 && (
                      <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.62rem' }}>
                        {discussions.length}
                      </Badge>
                    )}
                  </span>
                }
              >
                <div className="mt-3 mb-5">
                  {discussions.length > 0 ? discussions.map(disc => (
                    <div key={disc.guid} className="disc-row">
                      <div className="disc-avatar">{getInitials(disc.user)}</div>
                      <div className="disc-row__body">
                        <div className="disc-row__title">
                          {disc.is_pinned === 1 && (
                            <Badge bg="warning" text="dark" className="me-1" style={{ fontSize: '0.62rem' }}>
                              📌 Pinned
                            </Badge>
                          )}
                          {disc.title}
                        </div>
                        <div className="disc-row__meta">
                          <span>
                            <FaCommentAlt className="me-1" />
                            {disc.comments_count} comment{disc.comments_count !== 1 ? 's' : ''}
                          </span>
                          {disc.last_comment_at && (
                            <span>Last activity {relativeTime(disc.last_comment_at)}</span>
                          )}
                        </div>
                      </div>
                      <div className="disc-row__action">
                        <OverlayTrigger placement="left" overlay={<Tooltip>Open discussion</Tooltip>}>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            href={`/listener-podcast-club/${podcastClub.guid}/discussion/${disc.guid}`}
                          >
                            <FaExternalLinkAlt />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-5 text-muted">
                      <FaComments style={{ fontSize: '2.5rem', opacity: 0.2 }} />
                      <p className="mt-3 mb-0">No discussions for this episode yet.</p>
                    </div>
                  )}
                </div>
              </Tab>

            </Tabs>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div className="ep-sidebar">

              {/* Club card */}
              <Card className="ep-sidebar__club-card">
                {heroImage && (
                  <img
                    src={heroImage}
                    alt={podcastClub.title}
                    className="ep-sidebar__club-card-img"
                  />
                )}
                <div className="ep-sidebar__club-card-body">
                  <div className="ep-sidebar__club-card-title">{podcastClub.title}</div>
                  {skills.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {skills.map(skill => (
                        <Badge
                          key={skill.id}
                          bg="light"
                          text="dark"
                          className="border"
                          style={{ fontSize: '0.68rem', fontWeight: 500 }}
                        >
                          {skill.title}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              {/* Discussions preview */}
              {discussions.length > 0 && (
                <Card className="border-0 shadow-sm" style={{ borderRadius: '14px' }}>
                  <Card.Body className="p-3">
                    <div className="ep-sidebar__section-label">Discussions</div>
                    {discussions.slice(0, 3).map(disc => (
                      <a
                        key={disc.guid}
                        href={`/listener-podcast-club/${podcastClub.guid}/discussion/${disc.guid}`}
                        className="ep-sidebar__disc-item"
                      >
                        <div className="disc-avatar disc-avatar--sm">{getInitials(disc.user)}</div>
                        <div>
                          <div className="ep-sidebar__disc-title">{disc.title}</div>
                          <div className="ep-sidebar__disc-meta">
                            <FaCommentAlt style={{ fontSize: '0.58rem', marginRight: '3px' }} />
                            {disc.comments_count} · {relativeTime(disc.last_comment_at)}
                          </div>
                        </div>
                      </a>
                    ))}
                    {discussions.length > 3 && (
                      <div className="mt-2 pt-1 text-center">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-muted"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => setActiveTab('discussions')}
                        >
                          +{discussions.length - 3} more discussions →
                        </button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

            </div>
          </Col>

        </Row>
      </div>

      {/* Practice modal */}
      <PracticeModal
        show={showPracticeModal}
        onHide={closePracticeModal}
        takeaway={selectedTakeaway}
        practice={selectedPractice}
        keyTakeawayId={selectedTakeawayId}
        podcastClub={podcastClub}
        onPracticeChange={handlePracticeChange}
        onPracticeDeleted={handlePracticeDeleted}
      />
    </LoadingFade>
  );
};

export default ListenerEpisode;
